// src/pages/Admin/Finance/PaymentOptions/PaymentOptions.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./PaymentOptions.module.scss";

const IconEdit = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
    <path
      d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25zm2.92 2.83H5v-.92l9.06-9.06.92.92L5.92 20.08zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.52 1.52 3.75 3.75 1.52-1.52z"
      fill="currentColor"
    />
  </svg>
);

const IconPower = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
    <path
      d="M11 2h2v10h-2V2zm6.36 3.64 1.41 1.41A9 9 0 1 1 5.22 7.05l1.41-1.41A7 7 0 1 0 17.36 5.64z"
      fill="currentColor"
    />
  </svg>
);

const PaymentOptions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setErr(null);
      const res = await api.get("/v1/payment-options");
      setItems(res.data ?? []);
    } catch (e) {
      console.error("Failed to load payment options:", e);
      setErr("Couldn't load payment options.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleToggleActive = async (opt) => {
    try {
      const path = opt.isActive
        ? `/v1/payment-options/${opt._id}/deactivate`
        : `/v1/payment-options/${opt._id}/activate`;

      const res = await api.patch(path);
      const updated = res.data;

      setItems((prev) => prev.map((p) => (p._id === opt._id ? updated : p)));
    } catch (e) {
      console.error("Failed to toggle status:", e);
      setErr("Failed to update payment option status.");
    }
  };

  const handleEdit = (id) => {
    navigate(`/admin/finance/payment-options/${id}`);
  };

  const handleCreate = () => {
    navigate(`/admin/finance/payment-options/create`);
  };

  return (
    <div className={cls.wrap}>
      <header className={cls.header}>
        <div className={cls.headLeft}>
          <h1 className={cls.pageTitle}>Payment Options</h1>
          <p className={cls.pageSub}>Manage your available payment methods</p>
        </div>

        <div className={cls.controls}>
          <button type="button" className={cls.primaryBtn} onClick={handleCreate}>
            + New Payment Option
          </button>
        </div>
      </header>

      {err && <div className={cls.error}>{err}</div>}

      <div className={cls.tableWrap} aria-busy={loading ? "true" : "false"}>
        <table className={cls.table}>
          <thead>
            <tr>
              <th>Payment Option</th>
              <th className={cls.thActions}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              [...Array(6)].map((_, i) => (
                <tr key={`sk-${i}`} className={cls.skelRow}>
                  <td colSpan={2}>
                    <div className={cls.skel} />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={2} className={cls.empty}>
                  No payment options found.
                </td>
              </tr>
            ) : (
              items.map((opt) => (
                <tr key={opt._id} className={cls.row}>
                  <td className={cls.titleCell}>
                    <div className={cls.titleTop}>
                      <span className={cls.titleText}>{opt.title}</span>
                    </div>

                    <div className={cls.metaBadges}>
                      <span className={`${cls.badge} ${cls.badgeKey}`}>
                        {opt.name}
                      </span>

                      <span
                        className={`${cls.badge} ${
                          opt.isActive ? cls.badgeActive : cls.badgeInactive
                        }`}
                      >
                        {opt.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </td>

                  <td className={cls.actionsCell}>
                    <div className={cls.actions}>
                      <button
                        type="button"
                        className={`${cls.iconBtn} ${
                          opt.isActive ? cls.iconDanger : cls.iconSuccess
                        }`}
                        onClick={() => handleToggleActive(opt)}
                        title={opt.isActive ? "Deactivate" : "Activate"}
                        aria-label={opt.isActive ? "Deactivate" : "Activate"}
                      >
                        <IconPower />
                      </button>

                      <button
                        type="button"
                        className={`${cls.iconBtn} ${cls.iconInfo}`}
                        onClick={() => handleEdit(opt._id)}
                        title="Edit"
                        aria-label="Edit"
                      >
                        <IconEdit />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <footer className={cls.footer}>
        <span className={cls.pageInfo}>
          Total payment options: <strong className={cls.total}>{items.length}</strong>
        </span>
      </footer>
    </div>
  );
};

export default PaymentOptions;
